import { createContext, use } from 'react';

import type { NameTable } from '../op-meta.ts';
import type { DatasetProvider } from '../providers/types.ts';
import type { ProviderEntry, ProviderResolver } from '../providers/useProviderRegistry.ts';
import type { IdentityTable } from '../schema/row-identity.ts';

/**
 * React-context-supplied extensions to the engine's `EvalContext`.
 * Threaded via context so consumer hooks (`useEvalExpr`,
 * `useMaterialize`, `useMaterializedExpr`) reach them without a
 * per-call options bag — the call site that builds the deps mounts
 * `<EvalDepsContext value={deps}>` once at the conversation root, and
 * every descendant evaluator hook picks them up.
 *
 * Every field is optional; `null` context resolves to engine defaults
 * (`defaultOpTable`, no provider registry, no cognite callbacks).
 * Production wiring lives in the chat plugin's
 * `<ChatSessionProvider>`; tests mount their own provider via the
 * shared `withSessionContext` helper.
 */
export interface EvalDeps {
  /**
   * Custom op table for the evaluator (typically engine `builtins`
   * composed with domain ops like the transform set). Omit to pick
   * up `defaultOpTable` from `evalExpr`.
   */
  readonly ops?: NameTable;
  /**
   * Dataset-provider resolver by `(group, name)`. The source ops
   * (`query` / `datapoints`) resolve the owning provider through this at
   * op-apply and the static-check pass reads the same resolver to type a
   * source op's declared schema. Built once per `(client, views)` by the
   * host and shared with `getProviderById` from one registry snapshot.
   */
  readonly getProvider?: ProviderResolver;
  /**
   * Dataset-provider resolver by provider `id`. The materialiser
   * dispatches a `ProviderSource` to its owning provider's `fetch`
   * through this — a `ProviderSource` carries only `providerId`, not the
   * group/name it was minted from. The same registry snapshot as
   * `getProvider`.
   */
  readonly getProviderById?: (id: string) => DatasetProvider | undefined;
  /**
   * The composed {@link IdentityTable} of every provider-contributed
   * row-identity contract (today: the CDF instance identity). Threaded onto
   * the `EvalContext` so the type-check pass recognises direct-relation cells
   * and the runtime dedups identity vectors by `identity.key`. Omit to fall
   * back to `EMPTY_IDENTITY_TABLE` (claims nothing).
   */
  readonly identities?: IdentityTable;
  /**
   * Every claimable view name in the knowledge-graph group — the runtime
   * catalogue the DescribeViews tool draws its view enum from. A reactive
   * value (not a callback): the host recomputes it from the live provider
   * registry on each render, so render-time consumers re-render when an
   * application provider registers. The tool resolves each name's provider
   * via `getProvider` and renders its `describe`.
   */
  readonly viewNames?: readonly string[];
  /**
   * Register an application provider into the knowledge-graph group at
   * runtime, ahead of the CDF view provider (so a facade claiming a name
   * wins over a same-named CDF view). Idempotent by `provider.id`. The
   * host mounts a live registry behind this; components inside the session
   * register via `useAppProvider`. Absent when no host registry is mounted.
   */
  readonly registerProvider: (entries: ProviderEntry[]) => void;
}

/**
 * React context carrying the optional engine extensions described by
 * {@link EvalDeps}. `null` default means "use engine defaults"; the
 * consumer hook reads through {@link useEvalDeps} which converts
 * `null` to a stable empty object.
 *
 * Mount with React 19's context-as-provider syntax:
 *
 * ```tsx
 * <EvalDepsContext value={{ ops, getProvider, getProviderById }}>
 *   {children}
 * </EvalDepsContext>
 * ```
 */
export const EvalDepsContext = createContext<EvalDeps>({
  registerProvider: () => () => {
    // No-op cleanup when used outside provider
  },
});

/**
 * Read the current {@link EvalDeps} from React context. Returns a
 * stable empty object when no provider is mounted, so consumers can
 * destructure without null-guarding.
 *
 * Unlike `useSession()`, this hook does not throw on a missing
 * provider — sensible defaults exist for every field, so missing
 * context just means "use them."
 */
export function useEvalDeps(): EvalDeps {
  return use(EvalDepsContext);
}
